import { extend } from 'vee-validate';
import { required, email, min_value, numeric, integer, regex, max_value } from 'vee-validate/dist/rules';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import i18n from '@/i18n';
import store from '@/store';

// NOTE: Messages are functions so they translate at validation time

extend('regex', {
	...regex,
	message: () => i18n.t('utils.validator.regex')
});

extend('postal_code', {
	validate: (value) => {
		// Accept US (12345 / 12345-6789), Canadian (K1K1Y1 / K1K 1Y1) and UK (SW1A 1AA) formats
		const regex = /^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/;
		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.postal_code')
});

extend('required', {
	...required,
	message: () => i18n.t('utils.validator.required')
});

extend('email', {
	...email,
	message: () => i18n.t('utils.validator.email')
});

extend('min', {
	validate(value, args) {
		return value.length >= args.length;
	},
	params: ['length'],
	message: (field, values) => i18n.t('utils.validator.min', values)
});

extend('max', {
	validate(value, args) {
		return value.length <= args.length;
	},
	params: ['length'],
	message: (field, values) => i18n.t('utils.validator.max', values)
});

extend('min_value', {
	...min_value,
	message: (field, values) => i18n.t('utils.validator.min_value', values)
});

extend('max_value', {
	...max_value,
	message: (field, values) => i18n.t('utils.validator.max_value', values)
});

extend('numeric', {
	...numeric,
	message: () => i18n.t('utils.validator.numeric')
});

extend('integer', {
	...integer,
	message: () => i18n.t('utils.validator.integer')
});

extend('decimal', {
	//@ts-ignore
	validate: (value, { decimals = '*', separator = '.' } = {}) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		if (Number(decimals) === 0) {
			return {
				valid: /^-?\d*$/.test(value)
			};
		}
		const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
		const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`);

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.decimal')
});

extend('greater_than', {
	params: ['target', 'field1-name', 'field2-name'],
	// TO DO: FIX ts error
	//@ts-ignore
	validate(value, { target }) {
		return value > target;
	},
	message: (field, values) => i18n.t('utils.validator.greater_than', { field1: values['field1-name'], field2: values['field2-name'] })
});

// TO DO: FIX ts error
//@ts-ignore
extend('expiry_date', {
	//@ts-ignore
	validate: (date) => {
		if(date.length === 5) {
			const d = new Date();
			const currentYear = d.getFullYear();
			const currentMonth = d.getMonth() + 1;
			const parts = date.split('/');
			const year = parseInt(parts[1], 10) + 2000;
			const month = parseInt(parts[0], 10);
			if (year < currentYear || month > 12 || year > currentYear + 6 || (year == currentYear && month < currentMonth)) {
				return {
					valid: false
				};
			}
			return {
				valid: true
			};
		}
	},
	message: () => i18n.t('utils.validator.expiry_date')
});

extend('valid_credit_card', {
	validate: (cardNumber) => {
		cardNumber = cardNumber.replace(/\D/g,'');
		const len = cardNumber.length;
		const parity = len % 2;
		let sum = 0;
		for (let i = len - 1; i >= 0; i--) {
			let d = parseInt(cardNumber.charAt(i));
			if(i % 2 == parity) {
				d *= 2;
			}
			if(d > 9) {
				d -= 9;
			}
			sum += d;
		}
		if((sum % 10) === 0) {
			return {
				valid: true
			};
		}
		return {
			valid: false
		};
	},
	message: () => i18n.t('utils.validator.valid_credit_card')
});

extend('password_contains_general', {
	validate: (value) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		const regex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$');

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.password_contains_general')
});

extend('password_contains_uppercase', {
	validate: (value) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		const regex = new RegExp('(?=.*[A-Z])');

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.password_contains_uppercase')
});

extend('password_contains_lowercase', {
	validate: (value) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		const regex = new RegExp('(?=.*[a-z])');

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.password_contains_lowercase')
});

extend('password_contains_number', {
	validate: (value) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		const regex = new RegExp('.*[0-9].*');

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.password_contains_number')
});

extend('password_contains_special_character', {
	validate: (value) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		const regex = new RegExp('^(?=.*?[@$!%*?&])');

		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.password_contains_special_character')
});

extend('characters_for_name', {
	validate: (value) => {
		if (value[value.length - 1] === '-' || value[value.length - 1] === '\'') {
			return false;
		}

		const regex = /(?![\'-])^[A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜßŸ\'-\s]*$/i;
		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.characters_for_name')
});

extend('cardholder_name', {
	validate: (value) => {
		// FreedomPay is the authority on what it accepts, so keep this permissive:
		// cardholder names printed on cards routinely contain initials with periods
		// ("J. Smith Jr."), digits, and non-French diacritics ("Muñoz"). Allow any
		// letter, digit, space, and card-safe punctuation; reject only obvious garbage.
		const regex = /^[\p{L}\d .'\-,]+$/u;
		return {
			valid: regex.test(value)
		};
	},
	message: () => i18n.t('utils.validator.characters_for_name')
});

extend('phone', {
	validate: (value) => {
		if (!value) {
			return { valid: false };
		}

		// Remove all non-digit characters for validation
		const cleanValue = value.replace(/\D/g, '');

		// Support international format with + prefix (E.164 format)
		if (value.startsWith('+')) {
			return { valid: isValidPhoneNumber(value) };
		}

		// Support UK national format when the restaurant is in the UK (starts with 0, 10-11 digits)
		if (store.getters['restaurant/getCountry'] === 'GB') {
			return { valid: /^0\d{9,10}$/.test(cleanValue) };
		}

		// Support North American format (10-11 digits)
		return { valid: cleanValue.length >= 10 && cleanValue.length <= 11 };
	},
	message: () => i18n.t('utils.validator.phone')
});
